import { Component, EventEmitter, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { EmployerLeaveTypeDialog, LeaveSchedules, LeaveTypeForm, LeaveTypePost } from '@core/models/leave-admin/employers/employer-leave-type.model';
import { Observable, Subject } from 'rxjs';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ConstantsService, ErrorService, LayoutService } from '@core/services';
import { EmployerProfileStoreService } from '@core/services/employer-profile-store.service';
import { ManageEmployersService } from '@core/services/manage-employers.service';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { EmployerLeaveType, EmployerProfile } from '@core/models/leave-admin/employers/employer-profile.model';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { LeaveHoursBanksBuckets, SelectionOption, StiiraError } from '@core/models';
import { ReplaceBankNamesPipe } from '@shared/pipes/replace-bank-name.pipe';
import { isNumber } from '@microsoft/applicationinsights-core-js';
import { takeUntil } from 'rxjs/operators';
import { Banks, Buckets } from '@core/enums';
import { UpdateConfirmationDialog } from '@core/models/shared/update-confirmation-dialog.model';
import { UpdateConfirmationComponent } from '../update-confirmation/update-confirmation.component';

@Component({
    selector: 'app-add-edit-leave-type',
    templateUrl: './add-edit-leave-type.component.html',
    styleUrl: './add-edit-leave-type.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddEditLeaveTypeComponent {
  static readonly componentName = 'AddEditLeaveTypeComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  public form: FormGroup<LeaveTypeForm>;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();

  public bankOptions: SelectionOption[];
  public bucketOptions: SelectionOption[];
  public leaveScheduleOptions: SelectionOption[];

  public position = position;
  public onDragEnded = onDragEnded;
  
  private formChangeEmitted: boolean = false;
  private formInitValues: any;
  private destroy$: Subject<void> = new Subject<void>();

  public isMobile$: Observable<boolean>;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

    constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      employerProfile: EmployerProfile;
      leaveHoursBanksBuckets: LeaveHoursBanksBuckets;
      dialogOptions: EmployerLeaveTypeDialog;
      leaveType: EmployerLeaveType; // nullable (add)
      sysText: any;
    },
    private fb: FormBuilder,
    private layoutService: LayoutService,
    private dialogRef: MatDialogRef<AddEditLeaveTypeComponent>,
    private store: EmployerProfileStoreService,
    private dialog: MatDialog,
    private service: ManageEmployersService,
    private errorService: ErrorService,
    private replaceBankNames: ReplaceBankNamesPipe,
    private constants: ConstantsService
  ) { 
    this.form = this.fb.group<LeaveTypeForm>({
      name: this.fb.control(null),
      leaveCategory: this.fb.control(null),
      banks: this.fb.control([]),
      buckets: this.fb.control([]),
      leaveSchedule: this.fb.control(null),
      leavesRequireAllBankHours: this.fb.control(false),
      allowUse: this.fb.control(null),
    });
  }

  
  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close(false));

    this.setupForm(this.data.leaveType, this.data.employerProfile);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(overrideUpdateConf: boolean = false): void {
    this.form.markAsUntouched();
    const dto: LeaveTypePost = {
      overrideUpdateConf: overrideUpdateConf,
      employerId: this.data.employerProfile.employerId,
      leaveTypeId: this.data.leaveType?.id,
      name: this.buildLeaveTypeName(false),
      leaveCategory: this.form.controls.leaveCategory.value,
      banks: this.form.controls.banks.value,
      buckets: this.form.controls.buckets.value,
      leaveSchedule: this.form.controls.leaveSchedule.value,
      leavesRequireAllBankHours: this.form.controls.leavesRequireAllBankHours.value,
      allowUse: this.form.controls.allowUse.value
    }

    let postObs: Observable<EmployerProfile | UpdateConfirmationDialog>;
    if (!this.data.leaveType) {
      postObs = this.service.postLeaveTypeAdd(dto);
    } else {
      postObs = this.service.postLeaveTypeEdit(dto)
    }
    postObs
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res)=>{
        if (res.showUpdateConfirmationDialog) {
          this.openUpdateConfirmationDialog(res as UpdateConfirmationDialog);
        } else {
          this.store.employerProfile = res as EmployerProfile;
          this.dialogRef.close();
        }
    },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
  }

  public close(canNavigate: boolean): void {
    if (canNavigate) {
      this.dialogRef.close();
    } else {
      if (this.noChanges) {
        this.dialogRef.close();
      } else {
        this.openUnsavedChangesDialog();
      }
    }
  }

  private setFormErrors() {
    setTimeout(() => {
      this.formInitValues = {...this.form.value};
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          } else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }
        });
    }, 0);
  }

  private setupForm(lt: EmployerLeaveType, erp: EmployerProfile): void {
    this.form.controls.banks.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.form.controls.name.setValue(this.buildLeaveTypeName());
        if (res?.length > 1) {
          this.form.controls.leavesRequireAllBankHours.enable();
        } else {
          this.form.controls.leavesRequireAllBankHours.setValue(false);
          this.form.controls.leavesRequireAllBankHours.disable();
        }
      });

    if (lt != null) {
      this.form.patchValue({
        name: this.replaceBankNames.transform(lt.name, this.data.leaveHoursBanksBuckets),
        leaveCategory: lt.leaveCategory?.id as number,
        banks: lt.banks,
        buckets: lt.buckets,
        leaveSchedule: 
          lt.isContinuous === true ? LeaveSchedules.Continuous : 
          lt.isIntermittent === true ? LeaveSchedules.Intermittent : 
          null,
        leavesRequireAllBankHours: lt.leavesRequireAllBankHours ?? false,
        allowUse: lt.allowUse
      })
    } else {
      this.form.controls.allowUse.setValue(true);
    }

    this.bankOptions = this.buildBankOptions(erp);
    this.bucketOptions = this.buildBucketOptions(erp);
    this.leaveScheduleOptions = this.buildLeaveScheduleOptions();

    this.form.controls.buckets.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.form.controls.name.setValue(this.buildLeaveTypeName());
      });

    this.form.controls.leaveSchedule.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.form.controls.name.setValue(this.buildLeaveTypeName());
      });

    this.setFormErrors();
  }

  private buildLeaveTypeName(replaceBankNames: boolean = true): string {
    // build leave type name from replacement tokens + leave schedule (if applicable)
    // if exactly 2 banks/buckets are selected the will be joined by ' & ', otherwise if
    // 3 or more are selected they will be separted by ', '. If a schedule is 
    // selected it will be joined with ' - '.
    // for example:
    // FMLA & State - Continuous 
    // FMLA, State, PLO - Intermittent
    // ADA 

    let name = "";

    const bankKeys = this.form.controls.banks.value.map(v => {
      return Object.keys(Banks).find(key => Banks[key] === v);
    });

    const bucketKeys = this.form.controls.buckets.value.map(v => {
      return Object.keys(Buckets).find(key => Buckets[key] === v);
    });

    const allKeys = [...bankKeys, ...bucketKeys];

    allKeys.forEach((key, i) => {
      if (i === 1 && allKeys.length == 2) {
        name += " & "
      } else if (i > 0 && allKeys.length > 1) {
        name += ", "
      }
      switch(key) {
        case Banks[Banks.Bank1]:
          name += this.constants.BANK_REPLACEMENT_TOKENS.bank1;
          break;
        case Banks[Banks.Bank2]:
          name += this.constants.BANK_REPLACEMENT_TOKENS.bank2;
          break;
        case Banks[Banks.Bank3]:
          name += this.constants.BANK_REPLACEMENT_TOKENS.bank3;
          break;
        case Banks[Banks.Bank4]:
          name += this.constants.BANK_REPLACEMENT_TOKENS.bank4;
          break;
        case Banks[Banks.Bank5]:
          name += this.constants.BANK_REPLACEMENT_TOKENS.bank5;
          break;
        case Buckets[Buckets.Bucket1]:
          name += this.constants.BUCKET_REPLACEMENT_TOKENS.bucket1;
          break;
        case Buckets[Buckets.Bucket2]:
          name += this.constants.BUCKET_REPLACEMENT_TOKENS.bucket2;
          break;
        case Buckets[Buckets.Bucket3]:
          name += this.constants.BUCKET_REPLACEMENT_TOKENS.bucket3;
          break;
        case Buckets[Buckets.Bucket4]:
          name += this.constants.BUCKET_REPLACEMENT_TOKENS.bucket4;
          break;
      }
    });

    if (this.form.controls.banks.value?.length > 0 || this.form.controls.buckets.value?.length > 0) {
      if (this.form.controls.leaveSchedule.value === LeaveSchedules.Continuous) {
        name += " - " + this.data.sysText["employerLeaveTypes_leaveSchedules_" + LeaveSchedules.Continuous];
      } else if (this.form.controls.leaveSchedule.value === LeaveSchedules.Intermittent) {
        name += " - " + this.data.sysText["employerLeaveTypes_leaveSchedules_" + LeaveSchedules.Intermittent];
      }
      if (replaceBankNames) {
        name = this.replaceBankNames.transform(name, this.data.leaveHoursBanksBuckets)
      }
    }

    return name;
  }


  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.store.sysText.unsavedChanges,
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private buildBankOptions(erp: EmployerProfile): SelectionOption[] {
    return erp.leaveHoursBanks.map(lhb => {
      return {
        id: +lhb.id,
        description: lhb.primaryName ?? lhb.defaultName
      }
    });
  }

  private buildBucketOptions(erp: EmployerProfile): SelectionOption[] {
    return erp.leaveHoursBuckets.map(lhb => {
      return {
        id: +lhb.id,
        description: lhb.primaryName ?? lhb.defaultName
      }
    });
  }

  private buildLeaveScheduleOptions(): SelectionOption[] {
    const leaveSchedules = LeaveSchedules;
    const leaveScheduleIds = Object.values(leaveSchedules).filter(v => isNumber(v));
    let lsOptions: SelectionOption[] = [];
    leaveScheduleIds.forEach(id => {
      const desc: string = this.data.sysText['employerLeaveTypes_leaveSchedules_' + id];
      const option: SelectionOption = {
        id: id,
        description: desc
      };
      lsOptions.push(option);
    });
    return lsOptions;
  }

  private openUpdateConfirmationDialog(updateConfirmationDialog: UpdateConfirmationDialog): void {
    const dialogConfig: MatDialogConfig = {
      id: UpdateConfirmationComponent.componentName,
      width: "450px",
      panelClass: "mat-dialog-container-mobileWidth",
      disableClose: false,
      data: {
        sysText: this.store.sysText.updateConfirmation,
        updateConfirmationDialog: updateConfirmationDialog
      }
    };

    this.dialog.open(UpdateConfirmationComponent, dialogConfig)
      .beforeClosed().subscribe((res: boolean) => {
        if (res) {
          this.onSubmit(true);
        }
      });
  }
}
